import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { umStorage } from '@nexelem/services';
import {
  GanttShowOnTaskOption,
  GanttShowOnTaskOptions,
  KanbanShowOnTaskOption,
  KanbanShowOnTaskOptions,
  QueueColumnWidthOption,
  QueueColumnWidthOptions,
  TaskMainValueOption,
  TaskMainValueOptions,
} from './useProductionPlanSettings.types';

type ProductionPlanSettingsStoreState = {
  taskMainValue: TaskMainValueOption;
  kanban: {
    showOnTask: KanbanShowOnTaskOptions;
  };
  gantt: {
    showOnTask: GanttShowOnTaskOptions;
    showDependencies: boolean;
    ganttProductColors: boolean;
    queuesColumnWidth: QueueColumnWidthOption;
  };
};

type ProductionPlanSettingsStoreActions = {
  setTaskMainValue: (taskMainValue: TaskMainValueOption) => void;
  toggleGanttShowDependencies: () => void;
  setKanbanShowOnTask: (taskOption: KanbanShowOnTaskOption) => void;
  setGanttShowOnTask: (taskOption: GanttShowOnTaskOption) => void;
  setGanttQueuesColumnWidth: (width: QueueColumnWidthOption) => void;
  toggleGanttProductColors: () => void;
  reset: () => void;
};

export const useProductionPlanSettings = create(
  persist(
    immer<ProductionPlanSettingsStoreState & ProductionPlanSettingsStoreActions>((set, get, store) => {
      return {
        taskMainValue: TaskMainValueOptions.PRODUCT,
        kanban: {
          showOnTask: {
            assignedWorkers: true,
            productionTags: true,
            salesOrderIdClient: true,
            technology: true,
            time: true,
            productionOrderNumber: true,
            productionOrderExternalNumber: true,
          } satisfies KanbanShowOnTaskOptions,
        },
        gantt: {
          queuesColumnWidth: QueueColumnWidthOptions.SHORT,
          showDependencies: false,
          ganttProductColors: false,
          showOnTask: {
            salesOrderId: true,
            client: true,
            productionOrderNumber: true,
            productionOrderExternalNumber: true,
            progress: true,
            dueDate: true,
            time: true,
            assignedWorkers: true,
          } satisfies GanttShowOnTaskOptions,
        },
        toggleGanttProductColors: () =>
          set((state) => {
            state.gantt.ganttProductColors = !state.gantt.ganttProductColors;
          }),
        setTaskMainValue: (taskMainValue: TaskMainValueOption) =>
          set((state) => {
            state.taskMainValue = taskMainValue;
          }),
        toggleGanttShowDependencies: () =>
          set((state) => {
            state.gantt.showDependencies = !state.gantt.showDependencies;
          }),
        setKanbanShowOnTask: (taskOption: KanbanShowOnTaskOption) =>
          set((state) => {
            state.kanban.showOnTask[taskOption] = !state.kanban.showOnTask[taskOption];
          }),
        setGanttShowOnTask: (taskOption: GanttShowOnTaskOption) =>
          set((state) => {
            state.gantt.showOnTask[taskOption] = !state.gantt.showOnTask[taskOption];
          }),
        setGanttQueuesColumnWidth: (width: QueueColumnWidthOption) =>
          set((state) => {
            state.gantt.queuesColumnWidth = width;
          }),
        reset: () => {
          set(store.getInitialState());
        },
      };
    }),
    {
      name: 'production-plan-settings-store',
      storage: createJSONStorage(() => umStorage),
      // do not hydrate the store when user is not logged in, hydration is handled in Hydration.tsx component
      skipHydration: true,
    },
  ),
);
